import React, { FC, useEffect, useRef, useState } from 'react'
import { FormInstance, FormItemProps } from 'antd/lib/form'
import { Form as AntForm } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import FormItemLabel from 'antd/lib/form/FormItemLabel'

import { captchaActions } from '../../../../store/slices/captchaSlice'
import { RootState } from '../../../../store/store'
import { Input } from '../../index'
import Reload from '../../icons/reload'
import styles from './captcha.module.scss'

export type CaptchaProps = FormItemProps & {
  form: FormInstance
  error?: boolean
  inline?: boolean
  disabled?: boolean
  onRefresh?: () => void
}

const Captcha: FC<CaptchaProps> = (props) => {
  const { form, error, inline, disabled, onRefresh, ...itemProps } = props
  const wrapperRef = useRef<HTMLDivElement>(null)
  const captcha = useSelector<RootState, Captcha | undefined>((state) => state.captcha.captcha)
  const loading = useSelector<RootState, boolean>((state) => state.captcha.loading)
  const [loadedImage, setLoadedImage] = useState<string>()
  const imageSrc = inline && loading ? undefined : captcha?.captchaBase64

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(captchaActions.fetchCaptcha())
  }, [dispatch])

  useEffect(() => {
    form.setFieldsValue({ captchaKey: inline && loading ? undefined : captcha?.captchaKey })
  }, [form, captcha, inline, loading])

  useEffect(() => {
    if (inline && !disabled && !loading && captcha?.captchaKey) {
      wrapperRef.current?.querySelector<HTMLInputElement>('input:not([hidden])')?.focus({ preventScroll: true })
    }
  }, [inline, disabled, loading, captcha])

  const refresh = () => {
    if (disabled || loading) return
    form.setFields([
      { name: itemProps.name || 'captchaValue', value: '', errors: [] },
      { name: 'captchaKey', value: undefined, errors: [] },
    ])
    setLoadedImage(undefined)
    onRefresh?.()
    dispatch(captchaActions.fetchCaptcha())
  }

  return (
    <div ref={wrapperRef} className={`${styles.wrapper} ${inline ? styles.inline : ''}`}>
      {!inline && <FormItemLabel htmlFor={'captchaValue'} prefixCls={'ant-form'} label={'Введите код с картинки'} />}
      <div className={styles.inputWrapper}>
        <img
          key={imageSrc}
          src={imageSrc}
          alt={'captcha'}
          className={inline ? `${styles.image} ${imageSrc && loadedImage === imageSrc ? styles.imageVisible : ''}` : ''}
          onLoad={() => setLoadedImage(imageSrc)}
          onError={() => setLoadedImage(undefined)}
        />
        {inline && (
          <button
            type="button"
            className={styles.refresh}
            aria-label="Сменить картинку"
            title="Сменить картинку"
            disabled={disabled || loading}
            onClick={refresh}
          >
            <Reload size={20} color="currentColor" className={loading ? styles.refreshSpinning : ''} />
          </button>
        )}
        <AntForm.Item required={true} {...itemProps}>
          <Input
            id={'captchaValue'}
            className={error ? styles.error : ''}
            placeholder={inline ? 'Код' : undefined}
            aria-label={inline ? 'Код с картинки' : undefined}
            autoFocus={inline}
            disabled={disabled || (inline && loading)}
          />
        </AntForm.Item>
        {error && (
          <div className={styles.errorMessage + ' ant-form-item-explain ant-form-item-explain-error'}>
            Вы неверно ввели символы. Попробуйте еще раз
          </div>
        )}
        <AntForm.Item required={true} {...itemProps} name={'captchaKey'} hidden>
          <Input hidden name={'captchaKey'} value={captcha?.captchaKey} />
        </AntForm.Item>
      </div>
    </div>
  )
}

Captcha.defaultProps = {
  name: 'captchaValue',
}

export default Captcha
